import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { DIALOG_TYPE } from '../../../../../../constants';
import { Button, Dialog } from '../../../../../../ui';
import { CustomSelect } from '../../../../../../ui/custom_react_select/custom_select'; // Replace with the actual path
import Tooltip from '../../../../../../ui/tooltip';
import { convertSecondsToTimeTaken } from '../../../../../../utils/helpers/time';
import { UserAssessment } from '../../../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../../../utils/types/react_select_types';
import { AgenticSessionsList } from '../../../../../agentic_assessment';
import commonStyles from '../../assigned_assessments.module.css';
import styles from './assigned_list_modal.module.css';

type AssignedListModalProps = {
  userAssessmentList: UserAssessment[];
  selectedUserAssessment: UserAssessment | undefined;
  setSelectedUserAssessment: (
    selectedAssessment: UserAssessment | undefined,
  ) => void;
  setModalContent: (content: number) => void;
  setIsPractice: (isPractice: boolean) => void;
  setAfterHelpModalContent: (content: number) => void;
};

export const AssignedListModal = ({
  userAssessmentList,
  selectedUserAssessment,
  setSelectedUserAssessment,
  setModalContent,
  setIsPractice,
  setAfterHelpModalContent,
}: AssignedListModalProps) => {
  const history = useHistory();
  const [selectedAssessment, setSelectedAssessment] = useState<
    SelectOptions | undefined
  >(
    selectedUserAssessment !== undefined
      ? {
        value: selectedUserAssessment?.assessment?.id,
        label: selectedUserAssessment?.assessment?.assessmentTitle,
      }
      : undefined,
  );

  const filteredAssessmentList = useMemo(() => {
    const CURRENT_TIME = Date.now() / 1000;

    const x = userAssessmentList.filter(
      (assessment) =>
        // assessment.assessment.totalScore !== 0 &&
        // assessment.assessment.totalTime !== 0 &&
        assessment.assessment.startDate < CURRENT_TIME &&
        assessment.assessment.endDate > CURRENT_TIME,
    );

    return userAssessmentList.filter(
      (assessment) =>
        // assessment.assessment.totalScore !== 0 &&
        // assessment.assessment.totalTime !== 0 &&
        assessment.assessment.startDate < CURRENT_TIME &&
        assessment.assessment.endDate > CURRENT_TIME,
    );
  }, [userAssessmentList]);

  useEffect(() => {
    if (filteredAssessmentList.length === 1) {
      setSelectedUserAssessment(filteredAssessmentList[0]);
    }
  }, [filteredAssessmentList]);

  const assessmentOptions = useMemo(
    () =>
      filteredAssessmentList.map((assessment: UserAssessment) => ({
        value: assessment.assessment.id,
        label: assessment.assessment.assessmentTitle,
      })),
    [filteredAssessmentList],
  );

  const handleAssessmentSelect = (selected: SelectOptions) => {
    const selectedUserAssessment = userAssessmentList.find(
      (userAssessment: UserAssessment) =>
        userAssessment.assessment.id === selected.value,
    );
    setSelectedUserAssessment(selectedUserAssessment);
    setSelectedAssessment(selected);
  };
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DIALOG_TYPE>();

  const openDialog = (type: DIALOG_TYPE) => {
    setDialogType(type);
    setIsDialogOpen(true);
  };

  const HELP_SEEN_KEY = 'hiacuity_help_seen';

  const handleStartFlow = (isPracticeMode: boolean) => {
    setIsPractice(isPracticeMode);

    // If starting a real assessment and there are multiple options, show details first
    if (!isPracticeMode && filteredAssessmentList.length > 1) {
      setModalContent(1);
      return;
    }

    const alreadySeen = localStorage.getItem(HELP_SEEN_KEY);
    if (!alreadySeen) {
      // First time — show help screens, then go to privacy (5)
      setAfterHelpModalContent(5);
      setModalContent(4);
    } else {
      // Already seen — go directly to privacy notice (5)
      setModalContent(5);
    }
  };

  // Stop all media streams when component mounts (after practice or assessment)
  useEffect(() => {
    const stopAllMediaStreams = () => {
      // Get all video and audio elements
      const videos = document.querySelectorAll('video');
      const audios = document.querySelectorAll('audio');

      // Stop all video streams
      videos.forEach((video) => {
        if (video.srcObject) {
          const stream = video.srcObject as MediaStream;
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          video.srcObject = null;
        }
      });

      // Stop all audio streams
      audios.forEach((audio) => {
        if (audio.srcObject) {
          const stream = audio.srcObject as MediaStream;
          stream.getTracks().forEach((track) => {
            track.stop();
          });
          audio.srcObject = null;
        }
      });
    };

    stopAllMediaStreams();

    // Cleanup on unmount
    return () => {
      stopAllMediaStreams();
    };
  }, []);

  return (
    <>
      <div className={commonStyles.modalContentContainer}>
        <h1 className='text-4xl font-bold text-gray-900 mb-4'>
          Welcome, { localStorage.getItem('firstname') }! 👋
        </h1>
        { localStorage.getItem('profilePic') === 'null' && (
          <p className='text-sm text-red-600 mb-3'>
            You are not allowed to take the assessment until you have taken the
            registration photo
          </p>
        ) }
        <AgenticSessionsList />
        { filteredAssessmentList.length === 1 && selectedUserAssessment ? (
          <div className='space-y-3 mt-4'>
            <p className='text-base font-semibold text-gray-900'>
              Assessment Name -{ ' ' }
              { selectedUserAssessment.assessment.assessmentTitle }
            </p>
            <p className='text-sm font-medium text-gray-600'>
              Assessment duration -{ ' ' }
              { convertSecondsToTimeTaken(
                selectedUserAssessment.assessment.totalTime,
                false,
              ) }{ ' ' }
              minutes
            </p>
            { selectedUserAssessment.assessmentUser.status === 'In Progress' && (
              <p className='text-sm font-medium text-red-600'>
                Questions remaining:{ ' ' }
                { selectedUserAssessment.assessmentUser.questionsRemaining }
              </p>
            ) }
            <p className='text-sm font-medium text-gray-600'>
              Assessment points - { selectedUserAssessment.assessment.totalScore }{ ' ' }
              points
            </p>
          </div>
        ) : filteredAssessmentList.length > 0 ? (
          <>
            <p className='text-sm text-gray-600 my-4'>
              Here are the assignments assigned to you by HiAcuity
            </p>
            <div className={styles.userAssessmentListItem}>
              <CustomSelect
                  name='assessment'
                  label='Select Assessment'
                  options={assessmentOptions}
                  value={selectedAssessment}
                  onChange={(selectedOption: any) =>
                    handleAssessmentSelect(selectedOption)
                  }
              />
              { selectedUserAssessment && (
                <div className='py-3 space-y-2'>
                  <div className='flex justify-between'>
                    <p className='text-sm font-medium text-gray-600'>
                      Assessment duration -{ ' ' }
                      { convertSecondsToTimeTaken(
                        selectedUserAssessment.assessment.totalTime,
                        false,
                      ) }{ ' ' }
                      minutes
                    </p>
                    { selectedUserAssessment.assessmentUser.status ===
                      'In Progress' && (
                      <p className='text-sm font-medium text-red-600'>
                        Questions remaining:{ ' ' }
                        {
                          selectedUserAssessment.assessmentUser
                            .questionsRemaining
                        }
                      </p>
                    ) }
                  </div>
                  <p className='text-sm font-medium text-gray-600'>
                    Assessment points -{ ' ' }
                    { selectedUserAssessment.assessment.totalScore } points
                  </p>
                </div>
              ) }
            </div>
          </>
        ) : (
          <p className='text-sm text-gray-600 my-4'>
            No active assignments currently assigned
          </p>
        ) }
        <div className='flex w-full items-center justify-between mt-5 border-t border-primary-background pt-5'>
          <div className='flex flex-row items-center gap-2'>
            <Button
                onClick={() => handleStartFlow(true)}
                variant='secondary'
                label='Practice Tutorial'
            >
              Practice Tutorial
            </Button>
            <Tooltip
                message='You can try a practice assessment'
                position='bottom'
            />

            { /* Turn Off Camera Button */ }
            <Button
                onClick={() => {
                  if (
                    window.confirm(
                      'This will reload the page to force stop the camera and microphone. Continue?',
                    )
                  ) {
                  // Try to stop all streams first
                    const videos = document.querySelectorAll('video');
                    const audios = document.querySelectorAll('audio');

                    videos.forEach((video) => {
                      if (video.srcObject) {
                        const stream = video.srcObject as MediaStream;
                        stream.getTracks().forEach((track) => track.stop());
                        video.srcObject = null;
                      }
                    });

                    audios.forEach((audio) => {
                      if (audio.srcObject) {
                        const stream = audio.srcObject as MediaStream;
                        stream.getTracks().forEach((track) => track.stop());
                        audio.srcObject = null;
                      }
                    });

                    // Force reload to completely release camera
                    setTimeout(() => {
                      window.location.reload();
                    }, 100);
                  }
                }}
                variant='secondary'
                label='🔴 Turn Off Camera (Reload)'
            />
          </div>
          { userAssessmentList.length > 0 && (
            <div className='flex flex-row items-center gap-1'>
              { localStorage.getItem('profilePic') === 'null' ? (
                <Button
                    onClick={() => history.push('/identity-check')}
                    variant='primary'
                    label='Take Registration Photo'
                >
                  Take Registration Photo
                </Button>
              ) : (
                <Button
                    onClick={() => handleStartFlow(false)}
                    variant='primary'
                    disabled={selectedUserAssessment === undefined}
                    label='Start Assessment'
                />
              ) }
              <Tooltip
                  message='You can attempt selected assessment'
                  position='bottom'
              />
            </div>
          ) }
        </div>
      </div>
    </>
  );
};
